/**
 * Facade for API calls.
 * Keeps networking concerns out of reducers.
 */

export type ApiGetJsonOptions = {
    accessToken?: string;
    fetchOptions?: RequestInit;
};

export type ApiWriteJsonOptions = ApiGetJsonOptions & {
    body?: unknown;
};

async function request<T = unknown>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    url: string,
    options: ApiWriteJsonOptions = {}
): Promise<T> {
    const { accessToken, fetchOptions, body } = options;

    const headers = new Headers(fetchOptions?.headers ?? undefined);
    headers.set('Accept', 'application/json');
    if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
    }
    if (body !== undefined) {
        headers.set('Content-Type', 'application/json');
    }

    const response = await fetch(url, {
        method,
        ...fetchOptions,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    const text = await response.text();
    const parsed = text ? JSON.parse(text) : null;

    if (!response.ok) {
        const detail = parsed && typeof parsed === 'object' ? (parsed as any).detail : undefined;
        // A few conflicts answer with a structured `detail` of `{code, message}`,
        // so the client can branch on a stable identifier instead of matching
        // on prose (see the backend's `app/core/errors.py`). Everything else
        // answers with a plain string. Passing the object straight to `Error`
        // would stringify it to "[object Object]", so unwrap it here.
        const structured = detail && typeof detail === 'object' && !Array.isArray(detail) ? detail : undefined;
        const message = typeof structured?.message === 'string' ? structured.message : undefined;
        const error = new Error(
            message ?? (typeof detail === 'string' ? detail : `HTTP ${response.status} when calling ${url}`)
        );
        (error as any).status = response.status;
        (error as any).url = url;
        (error as any).detail = detail;
        (error as any).code = typeof structured?.code === 'string' ? structured.code : undefined;
        throw error;
    }

    return parsed as T;
}

/**
 * Serializable error shape for thunks to pass to `rejectWithValue`, so reducers
 * can branch on `status` (e.g. 404 = "not a member" vs. a real failure) and show
 * the backend's `detail` message (e.g. last-admin-guard 409s) instead of a
 * generic string.
 */
export type ApiErrorPayload = {
    message: string;
    status?: number;
    detail?: string;
    /**
     * Stable identifier from a structured error body, e.g.
     * `aps_connection_revoked` or `invitation_already_pending`. Present only on
     * the few responses the backend marks this way; branch on it rather than on
     * the status code alone when one status has several meanings.
     */
    code?: string;
    /**
     * Whatever else a structured error body carries beyond `code` and
     * `message` (see the backend's `app/core/errors.py::conflict`), e.g. the
     * `object_id` of a resource already in the state that caused the conflict.
     */
    extra?: Record<string, unknown>;
};

function errorMessage(detail: unknown, fallback: string): string {
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail)) {
        const messages = detail
            .map((item) => item && typeof item === 'object' && typeof (item as any).msg === 'string' ? (item as any).msg : null)
            .filter((message): message is string => Boolean(message));
        if (messages.length > 0) return messages.join(' ');
    }
    return fallback;
}

export function toApiErrorPayload(error: unknown): ApiErrorPayload {
    if (error instanceof Error) {
        const detail = (error as any).detail;
        const structured = detail && typeof detail === 'object' && !Array.isArray(detail) ? detail : undefined;
        const { code: _code, message: _message, ...extra } = structured ?? {};
        return {
            message: errorMessage(detail, error.message),
            status: (error as any).status,
            detail: typeof detail === 'string' ? detail : undefined,
            code: typeof (error as any).code === 'string' ? (error as any).code : undefined,
            extra: Object.keys(extra).length > 0 ? extra : undefined,
        };
    }
    return { message: 'Unknown error' };
}

export const ApiFacade = {
    async getJson<T = unknown>(url: string, options: ApiGetJsonOptions = {}): Promise<T> {
        return request<T>('GET', url, options);
    },

    async postJson<T = unknown>(url: string, options: ApiWriteJsonOptions = {}): Promise<T> {
        return request<T>('POST', url, options);
    },

    async putJson<T = unknown>(url: string, options: ApiWriteJsonOptions = {}): Promise<T> {
        return request<T>('PUT', url, options);
    },

    async patchJson<T = unknown>(url: string, options: ApiWriteJsonOptions = {}): Promise<T> {
        return request<T>('PATCH', url, options);
    },

    async deleteJson<T = unknown>(url: string, options: ApiWriteJsonOptions = {}): Promise<T> {
        return request<T>('DELETE', url, options);
    },
};
